import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateInventoryManufacturer } from '../../../shared/models/api/inventory-manufacturer.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryManufacturerService } from '../../../shared/services/api/inventory-manufacturer.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { CreateInventoryManufacturerFormValue } from './inventory-manufacturer-create.models';

@Component({
  selector: 'opa-inventory-manufacturer-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-manufacturer-create.component.html',
  styleUrl: './inventory-manufacturer-create.component.scss',
})
export class InventoryManufacturerCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryManufacturerService = inject(InventoryManufacturerService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryManufacturerCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const manufacturer = this.formValueToManufacturer(formValue);
    this.subSink.sink = this.inventoryManufacturerService
      .createInventoryManufacturer(manufacturer)
      .pipe(
        tap({
          next: (createdManufacturer) => {
            this.toastManager.addMessage({
              text: `Manufacturer '${createdManufacturer.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateInventoryManufacturerFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateInventoryManufacturerFormValue>>({
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToManufacturer(
    form: CreateInventoryManufacturerFormValue,
  ): CreateInventoryManufacturer {
    const newManufacturer: CreateInventoryManufacturer = {
      description: form.description!,
      name: form.name!,
    };
    return newManufacturer;
  }
}
